import React from "react";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";

const AddressList = ({
  addresses,
  selectedFavouriteKey,
  savingFavouriteIndex,
  getFavouriteAddressLoading,

  getAddressKey,
  getAddressType,
  getAddressIcon,
  cleanAddressTypeFromAddress,
  handleSelectSavedFavourite,
}) => {
  return (
    <div className="rounded-[28px] bg-white p-5 shadow-[0_15px_40px_rgba(15,23,42,0.08)] md:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Your Addresses</h2>
          <p className="text-sm text-slate-500">
            Click any saved address to make it favourite
          </p>
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <MapPin size={21} />
        </div>
      </div>

      {getFavouriteAddressLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="rounded-3xl border border-slate-100 bg-slate-50 p-4"
            >
              <div className="mb-3 h-4 w-28 animate-pulse rounded bg-slate-200" />
              <div className="mb-2 h-3 w-full animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
      ) : addresses?.length > 0 ? (
        <div className="max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
          <div className="grid gap-4">
            {addresses.map((item, index) => {
              const favourite = selectedFavouriteKey === getAddressKey(item);

              const type = getAddressType(item);
              const Icon = getAddressIcon(type);

              const rawAddress =
                item?.address ||
                item?.fullAddress ||
                item?.location ||
                "Address not available";

              const address = cleanAddressTypeFromAddress(rawAddress);

              const lat =
                item?.latitude ||
                item?.lat ||
                item?.addressLatitude ||
                item?.address_latitude ||
                "";

              const lng =
                item?.longitude ||
                item?.lng ||
                item?.long ||
                item?.addressLongitude ||
                item?.address_longitude ||
                "";

              return (
                <button
                  type="button"
                  key={item?.id || item?.pk_id || index}
                  onClick={() => handleSelectSavedFavourite(item, index)}
                  className={`group w-full rounded-3xl border p-4 text-left transition ${
                    favourite
                      ? "border-blue-300 bg-blue-50 shadow-sm"
                      : "border-slate-100 bg-slate-50 hover:border-blue-100 hover:bg-blue-50/40"
                  }`}
                >
                  <div className="flex gap-4">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-md ${
                        favourite
                          ? "bg-blue-600 text-white"
                          : "bg-white text-blue-700"
                      }`}
                    >
                      {savingFavouriteIndex === index ? (
                        <Loader2 size={22} className="animate-spin" />
                      ) : (
                        <Icon size={22} />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-slate-900">{type}</h3>

                        {favourite && (
                          <span className="flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                            <CheckCircle2 size={12} />
                            Favourite
                          </span>
                        )}

                        {!favourite && (
                          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                            Click to favourite
                          </span>
                        )}
                      </div>

                      <p className="text-sm leading-6 text-slate-600">
                        {address}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {lat ? (
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                            Lat: {lat}
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600">
                            Lat missing
                          </span>
                        )}

                        {lng ? (
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                            Lng: {lng}
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600">
                            Lng missing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex min-h-[350px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-100 text-blue-700">
            <MapPin size={32} />
          </div>

          <h3 className="text-lg font-bold text-slate-900">
            No address added yet
          </h3>

          <p className="mt-2 max-w-[320px] text-sm text-slate-500">
            Search your address and save it with exact location.
          </p>
        </div>
      )}
    </div>
  );
};

export default AddressList;
